import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext.tsx';
import PostureXeLogo from '../components/PostureXeLogo.tsx';
import { LanguageSwitcher } from '../components/LanguageSwitcher.tsx';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(from || '/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      toast.success(t('login.successToast'));
      navigate(from || '/', { replace: true });
    } catch {
      toast.error(t('login.errorToast'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-regina-gray-50">
        <div className="fixed top-3 right-3 z-50">
          <LanguageSwitcher />
        </div>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-regina-red" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-regina-gray-50 to-white px-4">
      <div className="fixed top-3 right-3 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-regina-gray-200 p-8">
        <div className="flex justify-center mb-8">
          <PostureXeLogo />
        </div>
        <h1 className="text-2xl font-bold text-center text-regina-gray-800 mb-2">
          {t('login.pageTitle')}
        </h1>
        <p className="text-center text-regina-gray-600 text-sm mb-8">
          {t('login.pageSubtitle')}
        </p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-regina-gray-700 mb-1">
              {t('login.username')}
            </label>
            <input
              id="username"
              autoComplete="username"
              className="w-full rounded-lg border border-regina-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-regina-red focus:border-regina-red outline-none"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-regina-gray-700 mb-1">
              {t('login.password')}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="w-full rounded-lg border border-regina-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-regina-red focus:border-regina-red outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-lg bg-regina-red hover:bg-red-700 text-white font-semibold transition-colors disabled:opacity-60"
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
